import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import { createGetItemSchema } from './schemaUtils';

// Create and configure Ajv instance
const ajv = new Ajv({ allErrors: true, useDefaults: true });
addFormats(ajv);

/**
 * Read form data for a UI field path: top-level keys, dotted paths
 * (e.g. parameters.region), or one array index (e.g. credentials[0]).
 */
export const getFormValueForField = (formData, name) => {
  if (formData == null || name == null || name === '') return undefined;

  const arrayIndexMatch = /^([^.[]+?)\[(\d+)\]$/.exec(name);
  if (arrayIndexMatch) {
    const [, prop, indexStr] = arrayIndexMatch;
    const arr = formData[prop];
    const idx = Number(indexStr);
    return Array.isArray(arr) ? arr[idx] : undefined;
  }

  if (name.includes('.')) {
    const parts = name.split('.');
    let cur = formData;
    for (const p of parts) {
      if (cur == null || typeof cur !== 'object') return undefined;
      cur = cur[p];
    }
    return cur;
  }

  return formData[name];
};

/**
 * Resolve the root form schema to the JSON Schema for a single field path.
 */
export const resolveFieldSchema = (schema, name) => {
  if (!schema?.properties) return null;

  const arrayIndexMatch = /^([^.[]+?)\[(\d+)\]$/.exec(name);
  if (arrayIndexMatch) {
    const [, prop, indexStr] = arrayIndexMatch;
    const parent = schema.properties[prop];
    if (!parent || parent.type !== 'array') return null;
    const idx = Number(indexStr);
    const getItemSchema = createGetItemSchema(parent);
    const itemSchema = getItemSchema(idx);
    if (typeof itemSchema === 'boolean') {
      return { leafSchema: itemSchema };
    }
    if (itemSchema == null || typeof itemSchema !== 'object') return null;
    return { leafSchema: itemSchema };
  }

  if (name.includes('.')) {
    const parts = name.split('.');
    let sub = schema;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!sub?.properties?.[part]) return null;
      const node = sub.properties[part];
      if (i === parts.length - 1) {
        return { leafSchema: node };
      }
      if (node.type !== 'object' || !node.properties) return null;
      sub = node;
    }
    return null;
  }

  const leafSchema = schema.properties[name];
  if (leafSchema === undefined) return null;
  return { leafSchema };
};

/**
 * Validate the entire form data against the schema
 * @param {Object} schema - The JSON schema
 * @param {Object} data - The form data
 * @returns {Object} - Validation result with isValid flag and errors object
 */
export const validateForm = (schema, data) => {
  const validate = ajv.compile(schema);
  const isValid = validate(data);

  if (!isValid) {
    const errors = validate.errors.reduce((acc, error) => {
      const path = error.instancePath.substring(1); // Remove leading slash
      acc[path] = error.message;
      return acc;
    }, {});
    return { isValid: false, errors };
  }

  return { isValid: true, errors: {} };
};

/**
 * Validate a single field
 * @param {Object} schema - The JSON schema
 * @param {string} name - The field name
 * @param {any} value - The field value
 * @returns {string|null} - Error message or null if valid
 */
export const validateField = (schema, name, value) => {
  const resolved = resolveFieldSchema(schema, name);
  if (!resolved?.leafSchema) return null;

  const { leafSchema } = resolved;
  if (typeof leafSchema === 'boolean') {
    return leafSchema ? null : 'Not allowed';
  }

  const fieldSchema = {
    type: 'object',
    properties: {
      __f: leafSchema,
    },
    required: [],
  };

  let validate;
  try {
    validate = ajv.compile(fieldSchema);
  } catch (e) {
    console.warn('validateField: invalid or unsupported schema for', name, e);
    return null;
  }

  const isValid = validate({ __f: value });

  if (!isValid) {
    const error = validate.errors?.[0];
    if (!error) return 'Invalid value';
    if (error.keyword === 'pattern') {
      return `Invalid format: ${
        leafSchema.description || 'Must match the required pattern'
      }`;
    }
    return error.message;
  }

  return null;
};

/**
 * Custom validators for specific use cases
 */
const customValidators = {
  /**
   * Validate email format
   * @param {string} email - The email to validate
   * @returns {string|null} - Error message or null if valid
   */
  email: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? null : 'Invalid email format';
  },

  /**
   * Validate password strength
   * @param {string} password - The password to validate
   * @returns {string|null} - Error message or null if valid
   */
  passwordStrength: (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*]/.test(password)) {
      return 'Password must contain at least one special character (!@#$%^&*)';
    }
    return null;
  },

  /**
   * Validate a URL
   * @param {string} url - The URL to validate
   * @returns {string|null} - Error message or null if valid
   */
  url: (url) => {
    try {
      new URL(url);
      return null;
    } catch {
      return 'Invalid URL format';
    }
  },
};

/**
 * Apply custom validation to a field
 * @param {string} name - The field name
 * @param {any} value - The field value
 * @param {Object} customValidation - Custom validation rules
 * @returns {string|null} - Error message or null if valid
 */
export const applyCustomValidation = (name, value, customValidation) => {
  if (customValidation && customValidation[name]) {
    const validator = customValidators[customValidation[name]];
    if (validator) {
      return validator(value);
    }
  }
  return null;
};

/**
 * Combine JSON Schema validation with custom validation
 * @param {Object} schema - The JSON schema
 * @param {string} name - The field name
 * @param {any} value - The field value
 * @param {Object} customValidation - Custom validation rules
 * @returns {string|null} - Error message or null if valid
 */
export const validateFieldWithCustom = (
  schema,
  name,
  value,
  customValidation
) => {
  const schemaError = validateField(schema, name, value);
  if (schemaError) return schemaError;

  const customError = applyCustomValidation(name, value, customValidation);
  if (customError) return customError;

  return null;
};
